"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { SiteSafeImage } from "@/components/SiteSafeImage";
import type { CartDto, CartItemDto } from "@/lib/cart-types";
import styles from "./cart.module.css";

type Labels = {
  title: string;
  selectAll: string;
  product: string;
  unitPrice: string;
  quantity: string;
  operation: string;
  delete: string;
  deleteSelected: string;
  total: string;
  checkout: string;
  empty: string;
  goShopping: string;
  authRequired: string;
  unavailable: string;
  checkoutDisabled: string;
  selectedRequired: string;
  loginOrRegister?: string;
  renderDraftsTitle?: string;
  renderDraftsNotice?: string;
  renderDraftFile?: string;
  renderDraftConfig?: string;
  renderDraftCreated?: string;
  renderDraftContinue?: string;
  renderDraftRemove?: string;
  renderDraftCheckoutNotice?: string;
};

type ActionResult = {
  ok: boolean;
  status: string;
  message?: string;
};

type RenderCartDraft = {
  recordId: string;
  fileName: string;
  createdAt: string;
  config?: {
    modelPart?: string;
    metal?: string;
    craft?: string;
    gemstone?: string;
    hdr?: string;
  };
};

type RenderCartBinding = {
  id: string;
  legacyCartId?: number | null;
  renderConfigRecordId: string;
  renderConfigSnapshotId: string;
  quantity: number;
  fileName: string;
  previewImagePath?: string | null;
  createTime: string;
};

const CART_DRAFT_KEY = "insparkle-render-cart-drafts";

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const data = (await response.json()) as T;
  if (!response.ok) {
    throw data;
  }
  return data;
}

function itemSubtotal(item: CartItemDto) {
  return (item.unitPrice + item.accessoryPrice) * item.quantity;
}

function readRenderCartDrafts(): RenderCartDraft[] {
  try {
    const raw = window.localStorage.getItem(CART_DRAFT_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatDraftDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function describeDraftConfig(draft: RenderCartDraft, locale: string) {
  const config = draft.config || {};
  const parts = [config.modelPart, config.metal, config.craft, config.gemstone, config.hdr].filter(Boolean);
  if (parts.length === 0) {
    return locale === "en-US" ? "Configuration pending" : "待继续配置";
  }
  return parts.join(" / ");
}

export function CartClient({
  initialCart,
  labels,
  currency,
  locale
}: Readonly<{
  initialCart: CartDto;
  labels: Labels;
  currency: string;
  locale: string;
}>) {
  const router = useRouter();
  const [cart, setCart] = useState(initialCart);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialCart.items.filter((item) => item.selected && item.canCheckout).map((item) => item.cartId)
  );
  const [message, setMessage] = useState(initialCart.message || "");
  const [renderDrafts, setRenderDrafts] = useState<RenderCartDraft[]>([]);
  const [renderBindings, setRenderBindings] = useState<RenderCartBinding[]>([]);
  const incompleteRenderBindings = renderBindings.filter((binding) => binding.legacyCartId == null);
  const selectableItems = cart.items.filter((item) => item.canCheckout);
  const authHref = `/auth?mode=login&lang=${locale}&returnUrl=${encodeURIComponent(`/cart?lang=${locale}`)}`;
  const draftLabels = {
    title: labels.renderDraftsTitle || (locale === "en-US" ? "Render configuration drafts" : "模型渲染草稿"),
    notice:
      labels.renderDraftsNotice ||
      (locale === "en-US"
        ? "These are incomplete configurations left in this browser. Reopen one and add it to cart to receive server-verified pricing."
        : "这些是本浏览器遗留的未完成配置。请重新打开并加入购物车，系统会进行服务端核价。"),
    file: labels.renderDraftFile || (locale === "en-US" ? "File" : "文件"),
    config: labels.renderDraftConfig || (locale === "en-US" ? "Configuration" : "配置"),
    created: labels.renderDraftCreated || (locale === "en-US" ? "Created" : "创建时间"),
    continueConfig: labels.renderDraftContinue || (locale === "en-US" ? "Continue configuring" : "继续配置"),
    remove: labels.renderDraftRemove || (locale === "en-US" ? "Remove" : "移除"),
    checkoutNotice:
      labels.renderDraftCheckoutNotice ||
      (locale === "en-US"
        ? "Incomplete browser drafts are not cart items and cannot be checked out."
        : "浏览器未完成草稿不是购物车商品，不能直接结算。")
  };
  const cartActionText = locale === "en-US"
    ? {
        quantityUpdated: "Quantity updated.",
        itemDeleted: "Item removed from cart.",
        selectedDeleted: "Selected items removed from cart.",
        renderBindingDeleted: "Incomplete configuration removed.",
        failed: "The cart operation could not be completed. Refresh and try again."
      }
    : {
        quantityUpdated: "\u6570\u91cf\u5df2\u66f4\u65b0\u3002",
        itemDeleted: "\u5546\u54c1\u5df2\u4ece\u8d2d\u7269\u8f66\u79fb\u9664\u3002",
        selectedDeleted: "\u5df2\u79fb\u9664\u9009\u4e2d\u5546\u54c1\u3002",
        renderBindingDeleted: "\u672a\u5b8c\u6210\u914d\u7f6e\u5df2\u79fb\u9664\u3002",
        failed: "\u8d2d\u7269\u8f66\u64cd\u4f5c\u672a\u5b8c\u6210\uff0c\u8bf7\u5237\u65b0\u540e\u91cd\u8bd5\u3002"
      };

  function handleActionFailure(error: unknown) {
    const result = error as Partial<ActionResult> | undefined;
    if (result?.status === "auth-required") {
      router.push(authHref);
      return;
    }
    setMessage(result?.status === "invalid" ? cartActionText.failed : labels.unavailable);
  }

  useEffect(() => {
    setRenderDrafts(readRenderCartDrafts());
    void fetch("/api/render/persistence/cart", { cache: "no-store" })
      .then(async (response) => (response.ok ? ((await response.json()) as RenderCartBinding[]) : []))
      .then(setRenderBindings)
      .catch(() => setRenderBindings([]));
  }, []);

  const allSelected = selectableItems.length > 0 && selectableItems.every((item) => selectedIds.includes(item.cartId));
  const selectedItems = useMemo(
    () => cart.items.filter((item) => selectedIds.includes(item.cartId) && item.canCheckout),
    [cart.items, selectedIds]
  );
  const selectedTotal = selectedItems.reduce((sum, item) => sum + itemSubtotal(item), 0);

  function toggleAll(next: boolean) {
    setSelectedIds(next ? selectableItems.map((item) => item.cartId) : []);
  }

  function toggleOne(cartId: string, next: boolean) {
    setSelectedIds((current) => (next ? Array.from(new Set([...current, cartId])) : current.filter((id) => id !== cartId)));
  }

  async function reloadCart() {
    const next = await requestJson<CartDto>("/api/products/cart", { cache: "no-store" });
    setCart(next);
    setSelectedIds(next.items.filter((item) => item.selected && item.canCheckout).map((item) => item.cartId));
    window.dispatchEvent(new Event("cart:refresh"));
  }

  async function updateQuantity(item: CartItemDto, quantity: number) {
    if (quantity < 1) {
      return;
    }
    setMessage("");
    try {
      const result = await requestJson<ActionResult>(`/api/products/cart/items/${encodeURIComponent(item.cartId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity })
      });
      setMessage(cartActionText.quantityUpdated);
      await reloadCart();
    } catch (error) {
      handleActionFailure(error);
    }
  }

  async function deleteItem(cartId: string) {
    setMessage("");
    try {
      const result = await requestJson<ActionResult>(`/api/products/cart/items/${encodeURIComponent(cartId)}`, {
        method: "DELETE",
      });
      setMessage(cartActionText.itemDeleted);
      await reloadCart();
    } catch (error) {
      handleActionFailure(error);
    }
  }

  async function deleteSelected() {
    if (selectedIds.length === 0) {
      setMessage(labels.selectedRequired);
      return;
    }
    setMessage("");
    try {
      const result = await requestJson<ActionResult>("/api/products/cart/items", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cartIds: selectedIds })
      });
      setMessage(cartActionText.selectedDeleted);
      await reloadCart();
    } catch (error) {
      handleActionFailure(error);
    }
  }

  function removeRenderDraft(recordId: string) {
    const next = renderDrafts.filter((draft) => draft.recordId !== recordId);
    setRenderDrafts(next);
    window.localStorage.setItem(CART_DRAFT_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event("cart:refresh"));
  }

  async function deleteRenderBinding(bindingId: string) {
    setMessage("");
    try {
      const result = await requestJson<ActionResult>(`/api/render/persistence/cart/${encodeURIComponent(bindingId)}`, {
        method: "DELETE",
      });
      setRenderBindings((current) => current.filter((binding) => binding.id !== bindingId));
      setMessage(cartActionText.renderBindingDeleted);
      window.dispatchEvent(new Event("cart:refresh"));
    } catch (error) {
      handleActionFailure(error);
    }
  }

  function checkout() {
    if (selectedItems.length === 0) {
      setMessage(labels.selectedRequired);
      return;
    }
    const ids = selectedItems.map((item) => item.cartId).join(",");
    router.push(`/checkout?lang=${locale}&cartIds=${encodeURIComponent(ids)}`);
  }

  const renderDraftSection =
    renderDrafts.length > 0 ? (
      <div className={styles.renderDrafts}>
        <h2>{draftLabels.title}</h2>
        <p className={styles.draftNotice}>{draftLabels.notice}</p>
        <div className={styles.tableScroll}>
          <table className={styles.draftTable}>
            <thead>
              <tr>
                <th>{draftLabels.file}</th>
                <th>{draftLabels.config}</th>
                <th>{draftLabels.created}</th>
                <th>{labels.operation}</th>
              </tr>
            </thead>
            <tbody>
              {renderDrafts.map((draft) => (
                <tr key={draft.recordId}>
                  <td>{draft.fileName}</td>
                  <td className={styles.draftMeta}>{describeDraftConfig(draft, locale)}</td>
                  <td>{formatDraftDate(draft.createdAt)}</td>
                  <td>
                    <span className={styles.draftActions}>
                      <Link href={`/render-editor/${draft.recordId}?lang=${locale}`}>{draftLabels.continueConfig}</Link>
                      <button type="button" onClick={() => removeRenderDraft(draft.recordId)} className={styles.linkButton}>
                        {draftLabels.remove}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.draftNotice}>{draftLabels.checkoutNotice}</p>
      </div>
    ) : null;

  const renderBindingSection =
    incompleteRenderBindings.length > 0 ? (
      <div className={styles.renderDrafts}>
        <h2>{locale === "en-US" ? "Incomplete database configurations" : "数据库未完成配置"}</h2>
        <p className={styles.draftNotice}>
          {locale === "en-US"
            ? "Reopen and add an item to cart to create a server-priced checkout line."
            : "请重新打开并加入购物车，以生成服务端核价后的可结算商品行。"}
        </p>
        <div className={styles.tableScroll}>
          <table className={styles.draftTable}>
            <thead>
              <tr>
                <th>{draftLabels.file}</th>
                <th>{draftLabels.config}</th>
                <th>{labels.quantity}</th>
                <th>{labels.operation}</th>
              </tr>
            </thead>
            <tbody>
              {incompleteRenderBindings.map((binding) => (
                <tr key={binding.id}>
                  <td>{binding.fileName}</td>
                  <td className={styles.draftMeta}>{binding.renderConfigSnapshotId.slice(0, 8)}</td>
                  <td>{binding.quantity}</td>
                  <td>
                    <span className={styles.draftActions}>
                      <Link href={`/render-editor/${binding.renderConfigRecordId}?lang=${locale}`}>{draftLabels.continueConfig}</Link>
                      <button type="button" onClick={() => deleteRenderBinding(binding.id)} className={styles.linkButton}>
                        {draftLabels.remove}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;

  if (cart.source === "auth-required") {
    return (
      <section className={styles.page} aria-label={labels.title}>
        <div className={styles.topLine} />
        <div className={styles.wrap}>
          <h1 className={styles.title}>{labels.title}</h1>
          {renderBindingSection}
          {renderDraftSection}
          <div className={styles.stateBox}>
            <p>{labels.authRequired}</p>
            <Link href={authHref}>{labels.loginOrRegister || (locale === "en-US" ? "Login / Register" : "登录 / 注册")}</Link>
          </div>
        </div>
      </section>
    );
  }

  if (cart.source === "unavailable") {
    return (
      <section className={styles.page} aria-label={labels.title}>
        <div className={styles.topLine} />
        <div className={styles.wrap}>
          <h1 className={styles.title}>{labels.title}</h1>
          {renderBindingSection}
          {renderDraftSection}
          <div className={styles.stateBox}>
            <p>{labels.unavailable}</p>
            <Link href={`/product-list?lang=${locale}`}>{labels.goShopping}</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.page} aria-label={labels.title}>
      <div className={styles.topLine} />
      <div className={styles.wrap}>
        <h1 className={styles.title}>{labels.title}</h1>
        {renderBindingSection}
        {renderDraftSection}
        {cart.items.length > 0 ? (
          <>
            <div className={styles.tableScroll}>
              <table className={styles.cartTable}>
                <thead>
                  <tr>
                    <th>
                      <label>
                        <input type="checkbox" checked={allSelected} onChange={(event) => toggleAll(event.target.checked)} />{" "}
                        {labels.selectAll}
                      </label>
                    </th>
                    <th>{labels.product}</th>
                    <th>{labels.unitPrice}</th>
                    <th>{labels.quantity}</th>
                    <th>{labels.operation}</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.items.map((item) => (
                    <tr key={item.cartId} className={item.canCheckout ? "" : styles.disabledRow}>
                      <td>
                        <input
                          type="checkbox"
                          disabled={!item.canCheckout}
                          checked={selectedIds.includes(item.cartId)}
                          onChange={(event) => toggleOne(item.cartId, event.target.checked)}
                        />
                      </td>
                      <td>
                        <div className={styles.productCell}>
                          <SiteSafeImage src={item.image || "/images/noimg.jpg"} unavailableLabel={locale === "en-US" ? "Image unavailable" : "图片资产未迁移"} />
                          <div>
                            <p>{item.name}</p>
                            <small>{item.optionText}</small>
                            {!item.canCheckout ? <small>{item.blockedReason || labels.checkoutDisabled}</small> : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        {currency} {itemSubtotal(item).toFixed(2)}
                      </td>
                      <td>
                        <span className={styles.quantityControl}>
                          <button type="button" onClick={() => updateQuantity(item, item.quantity - 1)} disabled={!item.canCheckout}>
                            -
                          </button>
                          <span>{item.quantity}</span>
                          <button type="button" onClick={() => updateQuantity(item, item.quantity + 1)} disabled={!item.canCheckout}>
                            +
                          </button>
                        </span>
                      </td>
                      <td>
                        <button type="button" onClick={() => deleteItem(item.cartId)} className={styles.linkButton}>
                          {labels.delete}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.summary}>
              <button type="button" onClick={deleteSelected} className={styles.linkButton}>
                {labels.deleteSelected}
              </button>
              <span className={styles.total}>
                {labels.total}: {currency} {selectedTotal.toFixed(2)}
              </span>
              <button type="button" onClick={checkout} className={styles.checkout}>
                {labels.checkout}
              </button>
            </div>
          </>
        ) : (
          <div className={styles.empty}>
            <p>{labels.empty}</p>
            <Link href={`/product-list?lang=${locale}`}>{labels.goShopping}</Link>
          </div>
        )}
        {message ? <p className={styles.message}>{message}</p> : null}
      </div>
    </section>
  );
}
